import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './InvigilatorDashboard.css'
import '../styles/dashboard-header.css'
import LoadingScreen from '../components/LoadingScreen'

const InvigilatorDashboard = () => {
  const [user, setUser] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const history = useHistory()

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }

    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    if (userData.role !== 'invigilator') {
      // Redirect to appropriate dashboard
      if (userData.role === 'admin') {
        history.push('/admin-dashboard')
      } else if (userData.role === 'teacher') {
        history.push('/teacher-dashboard')
      } else if (userData.role === 'volunteer') {
        history.push('/volunteer-dashboard')
      } else {
        history.push('/student-dashboard')
      }
      return
    }

    setUser(userData)
    fetchDashboardData()

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000)
    return () => clearInterval(interval)
  }, [history])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/invigilator/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDashboardData(data)
      } else {
        setError('Failed to load dashboard data')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    history.push('/login')
  }

  const handleViewAttempt = (attemptId) => {
    history.push(`/invigilator/attempt/${attemptId}`)
  }

  if (loading) {
    return <LoadingScreen message="Loading Invigilator Dashboard" />
  }

  if (error) {
    return (
      <div className="invigilator-dashboard-container">
        <div className="error">{error}</div>
      </div>
    )
  }

  return (
    <div className="invigilator-dashboard-container">
      <header className="invigilator-header">
        <div className="header-content">
          <h1>Invigilator Dashboard</h1>
          <div className="user-info">
            <span>Welcome, {user?.full_name}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="invigilator-main">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🔄</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.active_sessions || 0}</h3>
              <p>Active Sessions</p>
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-icon">⚠️</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.flagged_attempts || 0}</h3>
              <p>Flagged Attempts</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.completed_today || 0}</h3>
              <p>Completed Today</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">👁️</div>
            <div className="stat-content">
              <h3>Live</h3>
              <p>Monitoring</p>
            </div>
          </div>
        </div>

        <div className="invigilator-sections">
          <section className="active-sessions-section">
            <h2>Active Test Sessions</h2>
            <div className="sessions-list">
              {dashboardData?.active_attempts?.map((attempt) => (
                <div key={attempt.id} className="session-item">
                  <div className="session-info">
                    <h4>{attempt.student_name}</h4>
                    <p>Test: {attempt.test_title}</p>
                    <p>Email: {attempt.student_email}</p>
                    <p className={attempt.violation_count > 0 ? 'violations-warning' : ''}>
                      Violations: {attempt.violation_count || 0}
                    </p>
                  </div>
                  <div className="session-actions">
                    <button
                      className="view-button"
                      onClick={() => handleViewAttempt(attempt.id)}
                    >
                      Monitor
                    </button>
                  </div>
                </div>
              )) || <p>No active sessions</p>}
            </div>
          </section>

          <section className="flagged-section">
            <h2>Flagged Attempts</h2>
            <div className="flagged-list">
              {dashboardData?.flagged_attempts?.slice(0, 5).map((attempt) => (
                <div key={attempt.id} className="flagged-item">
                  <div className="flagged-info">
                    <h4>{attempt.student_name}</h4>
                    <p>Test: {attempt.test_title}</p>
                    <p className="violations-count">
                      {attempt.violation_count || 0} violations detected
                    </p>
                  </div>
                  <div className="flagged-actions">
                    <button
                      className="review-button"
                      onClick={() => handleViewAttempt(attempt.id)}
                    >
                      Review
                    </button>
                  </div>
                </div>
              )) || <p>No flagged attempts</p>}
            </div>
          </section>
        </div>

        <section className="recent-completed-section">
          <h2>Recently Completed</h2>
          <div className="completed-list">
            {dashboardData?.recent_completed?.map((attempt) => (
              <div key={attempt.id} className="completed-item">
                <div className="completed-info">
                  <h4>{attempt.student_name}</h4>
                  <p>Test: {attempt.test_title}</p>
                  <p>Score: {attempt.percentage}%</p>
                </div>
                <div className="completed-date">
                  {new Date(attempt.submitted_at).toLocaleString()}
                </div>
              </div>
            )) || <p>No recent completions</p>}
          </div>
        </section>

        <div className="action-buttons">
          <button
            className="primary-button"
            onClick={() => history.push('/invigilator/active-sessions')}
          >
            View All Active Sessions
          </button>
          <button
            className="secondary-button"
            onClick={() => history.push('/invigilator/flagged')}
          >
            View All Flagged Attempts
          </button>
        </div>
      </main>
    </div>
  )
}

export default InvigilatorDashboard
